import { BrowserRouter, Routes, Route, useParams, useNavigate } from 'react-router-dom';
import { App } from '@semantical/App';
import { Vocabulary } from '@semantical/modules/vocabulary';
import type { Workspace } from '@semantical/modules/semantical-core';
import type { WorkspaceConfigRoot } from '@semantical/modules/workspace';

type RouterProps = {
    vocabulary: Vocabulary;
    workspace: Workspace;
    workspaceConfig: WorkspaceConfigRoot;
};

/**
 * Wrapper component that extracts diagramId from URL params and passes to App.
 */
function DiagramRoute({ vocabulary, workspace, workspaceConfig }: RouterProps) {
    const { diagramId } = useParams<{ diagramId: string }>();
    const navigate = useNavigate();
    return (
        <App
            vocabulary={vocabulary}
            workspace={workspace}
            workspaceConfig={workspaceConfig}
            diagramId={diagramId!}
            navigateToDiagram={(id) => navigate(`/diagrams/${id}`)}
            navigateToVocabulary={(id) => navigate(`/vocabularies/${encodeURIComponent(id)}`)}
        />
    );
}

/**
 * Wrapper component that extracts vocabularyId from URL params and passes to App.
 */
function VocabularyRoute({ vocabulary, workspace, workspaceConfig }: RouterProps) {
    const { vocabularyId } = useParams<{ vocabularyId: string }>();
    const navigate = useNavigate();
    return (
        <App
            vocabulary={vocabulary}
            workspace={workspace}
            workspaceConfig={workspaceConfig}
            diagramId={undefined}
            vocabularyId={vocabularyId ? decodeURIComponent(vocabularyId) : undefined}
            navigateToDiagram={(id) => navigate(`/diagrams/${id}`)}
            navigateToVocabulary={(id) => navigate(`/vocabularies/${encodeURIComponent(id)}`)}
        />
    );
}

/**
 * Home route - no diagram active.
 */
function HomeRoute({ vocabulary, workspace, workspaceConfig }: RouterProps) {
    const navigate = useNavigate();
    return (
        <App
            vocabulary={vocabulary}
            workspace={workspace}
            workspaceConfig={workspaceConfig}
            diagramId={undefined}
            navigateToDiagram={(id) => navigate(`/diagrams/${id}`)}
            navigateToVocabulary={(id) => navigate(`/vocabularies/${encodeURIComponent(id)}`)}
        />
    );
}

/**
 * Main router component.
 *
 * Routes:
 * - /                    → App with no active diagram (editor closed)
 * - /diagrams/:id        → Load/create diagram by ID
 * - /vocabularies/:id    → View vocabulary by ID (URI encoded)
 */
export function AppRouter({ vocabulary, workspace, workspaceConfig }: RouterProps) {
    return (
        <BrowserRouter>
            <Routes>
                <Route path="/" element={<HomeRoute vocabulary={vocabulary} workspace={workspace} workspaceConfig={workspaceConfig} />} />
                <Route path="/diagrams/:diagramId" element={<DiagramRoute vocabulary={vocabulary} workspace={workspace} workspaceConfig={workspaceConfig} />} />
                <Route path="/vocabularies/:vocabularyId" element={<VocabularyRoute vocabulary={vocabulary} workspace={workspace} workspaceConfig={workspaceConfig} />} />
            </Routes>
        </BrowserRouter>
    );
}
